import { Injectable } from "@angular/core";
import { Contract } from "@edp-components/edp-contract";
import { DefineHouseServiceResponse } from "@edp-models/contract-define-house-v2.model";
import { ContractDefineHouseV2Service } from "@edp-services/contract-define-house-v2.service";
import {
    EDefineHouseActions,
    GetHouseEnergyComparison,
    GetHouseStatusMoonShot,
    SetAnswers,
    SetHouseStatusMoonShot,
    SetServiceAnswers,
} from "@edp-store/actions/define-house.action";
import { getContract } from "@edp-store/selectors/contract.selectors";
import { getHouseStatusMoonshot } from "@edp-store/selectors/define-house.selector";
import { IAppState } from "@edp-store/state/app.state";
import { Actions, Effect, ofType } from "@ngrx/effects";
import { select, Store } from "@ngrx/store";
import moment from "moment";
import { EMPTY, of } from "rxjs";
import { catchError, map, switchMap, take } from "rxjs/operators";

@Injectable()
export class DefineHouseEffects {
    @Effect()
    $loadContractData = this.actions$.pipe(
        ofType(EDefineHouseActions.GetServiceAnswers),
        switchMap(() =>
            this.store.pipe(
                select(getContract),
                take(1),
                switchMap((contract: Contract | null) => {
                    if (contract) {
                        return this.contractDefineHouseV2Service.getContractDefineData(contract.AccountNumber).pipe(
                            map(data => {
                                this.mountAnswers(data);
                                return new SetServiceAnswers(data);
                            }),
                            catchError(error => {
                                console.error("Error loading contract data", error);
                                return EMPTY;
                            })
                        );
                    }
                    return EMPTY;
                })
            )
        )
    );

    @Effect()
    $loadContractDataStatusMoonshot = this.actions$.pipe(
        ofType(EDefineHouseActions.GetHouseStatusMoonShot),
        switchMap((action: GetHouseStatusMoonShot) =>
            this.store.pipe(
                select(getContract),
                take(1),
                switchMap((contract: Contract | null) => {
                    if (contract) {
                        if (action.forceRequest) {
                            return this.getStatusMoonshot(contract.AccountNumber);
                        } else {
                            return this.store.pipe(
                                select(getHouseStatusMoonshot),
                                take(1),
                                switchMap(statusMoonShot => {
                                    if (statusMoonShot) {
                                        return of(new SetHouseStatusMoonShot(statusMoonShot));
                                    } else {
                                        return this.getStatusMoonshot(contract.AccountNumber);
                                    }
                                })
                            );
                        }
                    }
                    return EMPTY;
                })
            )
        )
    );

    constructor(
        private actions$: Actions,
        private store: Store<IAppState>,
        private contractDefineHouseV2Service: ContractDefineHouseV2Service
    ) {}

    private mountAnswers(data: DefineHouseServiceResponse[]): void {
        const formatedAnswers = data[0].house.map((answer: any) => {
            return {
                page: answer.page,
                category: answer.category,
                [answer.queryCode]: this.parseData(answer.data),
            };
        });

        this.store.dispatch(new SetAnswers(formatedAnswers));
    }

    private parseData(value: any): any {
        try {
            if (value === "true" || value === "false") {
                return value === "true";
            } else if (!isNaN(Number(value)) && value !== "") {
                return Number(value);
            } else if (Array.isArray(value) || typeof value === "object") {
                return value;
            }
            return value;
        } catch (error) {
            console.error("Failed to parse value:", value, error);
            return value;
        }
    }

    private getStatusMoonshot(account: string) {
        return this.contractDefineHouseV2Service.getContractDefineStatusMoonshot(account).pipe(
            map(data => {
                const statusMoonshot = data[0];
                if(statusMoonshot.completed && statusMoonshot.metricsAvailable) {
                    this.store.dispatch(
                        new GetHouseEnergyComparison({
                            date: moment().subtract(1, "month").format("YYYY-MM"),
                            forceRequest: true,
                        })
                    );
                }
                return new SetHouseStatusMoonShot(statusMoonshot)
            }),
            catchError(error => {
                console.error("Error loading status moonshot data", error);
                return EMPTY;
            })
        );
    }
}
